import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, Check, ArrowRight, Phone, MessageCircle, Shield, 
  FileText, Calendar, Users, BarChart3, Mail, Sparkles,
  ChevronDown, Star, Building2, Clock, Zap, Award, BookOpen, Languages,
  Send, Loader2, CheckCircle, Contact
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  
  // Demo request form state
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [demoForm, setDemoForm] = useState({
    nome_studio: '',
    nome_referente: '',
    cognome_referente: '',
    email: '',
    telefono: '',
    citta: '',
    numero_dipendenti: '',
    messaggio: ''
  });

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDemoSuccess(true);
        toast.success('Richiesta inviata con successo!');
      } else {
        toast.error(data.detail || 'Errore nell\'invio della richiesta');
      }
    } catch (error) {
      toast.error('Errore di connessione. Riprova più tardi.');
    } finally {
      setDemoLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: "Gestione Pratiche Completa",
      description: "Ogni pratica in un fascicolo digitale: documenti, parti coinvolte, scadenze, pagamenti e storico. Trova tutto in pochi click, dall'apertura alla stipula."
    },
    {
      icon: BookOpen,
      title: "Repertorio Digitale",
      description: "Registro atti sempre aggiornato e conforme. Numerazione automatica, ricerca istantanea, esportazione per il Consiglio Notarile. Addio registri cartacei."
    },
    {
      icon: Sparkles,
      title: "Editor Atti con AI",
      description: "Genera bozze automatiche, suggerimenti clausole e calcolo imposte. 5 template professionali inclusi."
    },
    {
      icon: Mail,
      title: "Caselle Email Condivise",
      description: "PEC e email dello studio accessibili a tutto il team. Sai sempre chi ha letto e chi ha risposto ad ogni messaggio. Niente più email perse o doppie risposte."
    },
    {
      icon: Calendar,
      title: "Agenda e Scadenze",
      description: "Calendario condiviso, promemoria automatici, gestione appuntamenti con i clienti."
    },
    {
      icon: Shield,
      title: "Zero Server, Zero Pensieri",
      description: "Dimentica server locali, backup manuali e tecnici IT. Tutto in cloud, sempre aggiornato, accessibile ovunque. Riduci i costi e lavora da qualsiasi dispositivo."
    },
    {
      icon: Users,
      title: "Task e Gestione Team",
      description: "Assegna compiti al personale e monitora l'avanzamento. Sai sempre chi sta lavorando su cosa, eviti sovrapposizioni e nulla viene dimenticato. Massima efficienza, zero confusione."
    },
    {
      icon: Languages,
      title: "Traduzione Atti Automatica",
      description: "Traduci atti e documenti in inglese, francese, tedesco, spagnolo e altre lingue con un click. Servi clienti stranieri senza attendere traduttori esterni. Ideale per compravendite internazionali e clienti non italofoni."
    },
    {
      icon: Contact,
      title: "Anagrafica Clienti",
      description: "Database completo di persone fisiche e giuridiche. Storico pratiche, documenti e contatti sempre a portata di mano. Cerca e trova qualsiasi cliente in un istante."
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "80",
      description: "Ideale per il notaio singolo",
      features: [
        "1 utente incluso",
        "Gestione pratiche illimitata",
        "Editor atti base",
        "3 template documenti",
        "1 casella PEC",
        "Supporto email",
        "Backup giornaliero"
      ],
      cta: "Inizia Ora",
      popular: false
    },
    {
      name: "Professional",
      price: "150",
      description: "Per studi strutturati",
      features: [
        "Fino a 5 utenti",
        "Tutte le funzionalità Starter",
        "Editor atti con AI",
        "Tutti i template documenti",
        "5 caselle PEC",
        "Gestione staff e timesheet",
        "Supporto prioritario",
        "API access"
      ],
      cta: "Prova Gratis 14 Giorni",
      popular: true
    },
    {
      name: "Enterprise",
      price: "250",
      description: "Per studi associati",
      features: [
        "Utenti illimitati",
        "Tutte le funzionalità Pro",
        "Multi-sede",
        "Caselle PEC illimitate",
        "Formazione dedicata",
        "Account manager",
        "SLA garantito 99.9%",
        "Personalizzazioni su richiesta"
      ],
      cta: "Contattaci",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Posso importare i dati dal mio gestionale attuale?",
      answer: "Sì, offriamo supporto per la migrazione dei dati dai principali gestionali notarili. Il nostro team ti assisterà gratuitamente nel processo di importazione."
    },
    {
      question: "I miei dati sono al sicuro?",
      answer: "Assolutamente. Utilizziamo crittografia end-to-end, server in Europa conformi GDPR, backup automatici giornalieri e autenticazione a due fattori opzionale."
    },
    {
      question: "Posso provare prima di acquistare?",
      answer: "Certo! Offriamo 14 giorni di prova gratuita del piano Professional, senza carta di credito richiesta. Potrai testare tutte le funzionalità."
    },
    {
      question: "Come funziona l'assistenza AI h24?",
      answer: "Il nostro assistente AI è disponibile 24 ore su 24 per rispondere alle tue domande, guidarti nell'uso della piattaforma e risolvere problemi comuni in tempo reale."
    },
    {
      question: "Posso cambiare piano in qualsiasi momento?",
      answer: "Sì, puoi fare upgrade o downgrade del tuo piano in qualsiasi momento. Le modifiche saranno effettive dal ciclo di fatturazione successivo."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Scale className="w-6 h-6 text-slate-700" />
              </div>
              <span className="text-xl font-light text-slate-800">Studio Notarile</span>
              <span className="text-xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">AI</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Funzionalità</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Prezzi</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
              <Button variant="outline" onClick={() => navigate('/login')} className="border-slate-300">
                Accedi
              </Button>
              <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700">
                Prova Gratis
              </Button>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
                Accedi
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              Potenziato dall'Intelligenza Artificiale
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Il Gestionale Notarile
              <span className="block bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                del Futuro è Qui
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Semplifica la gestione del tuo studio notarile con l'intelligenza artificiale. 
              Pratiche, atti, PEC e clienti in un'unica piattaforma progettata per i notai italiani.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-lg px-8 py-6 shadow-xl shadow-slate-300/30"
              >
                Prova Gratis 14 Giorni
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => window.open('https://robottino.telcavoip.net/it/outstanding/user/businesscard/?&id=152854922', '_blank')}
                className="text-lg px-8 py-6 border-slate-300"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Parla con l'AI
              </Button>
            </div>
            <p className="mt-6 text-sm text-slate-500">
              ✓ Nessuna carta di credito richiesta &nbsp;&nbsp; ✓ Setup in 5 minuti &nbsp;&nbsp; ✓ Assistenza h24
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-medium">Server in Europa</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="text-sm font-medium">Uptime 99.9%</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span className="text-sm font-medium">Backup Automatici</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tutto ciò che serve al tuo Studio
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Una suite completa di strumenti progettati specificamente per le esigenze dei notai italiani
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 rounded-2xl border border-slate-200 hover:border-amber-300 hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-amber-700" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Piani Semplici e Trasparenti
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Scegli il piano più adatto alle dimensioni del tuo studio. Upgrade o downgrade in qualsiasi momento.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-2xl shadow-slate-400/30 scale-105' 
                    : 'bg-white border border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 text-sm font-bold px-4 py-1 rounded-full">
                      Più Popolare
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 ${plan.popular ? 'text-slate-300' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                      €{plan.price}
                    </span>
                    <span className={plan.popular ? 'text-slate-400' : 'text-slate-500'}>/mese</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-amber-400' : 'text-teal-500'}`} />
                      <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full py-6 ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-900 font-bold' 
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                  }`}
                  onClick={() => plan.name === 'Enterprise' ? window.open('tel:+390497852233') : navigate('/register')}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-slate-500">
            Tutti i prezzi sono IVA esclusa. Fatturazione mensile o annuale (2 mesi gratis).
          </p>
        </div>
      </section>

      {/* Section 1: Sviluppato con i Notai */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                La Nostra Filosofia
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Sviluppato con i Notai,
                <span className="block text-amber-600">per i Notai</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Studio Notarile AI non è nato in un laboratorio tecnologico isolato. È il frutto di anni di ascolto, 
                collaborazione e confronto diretto con professionisti del notariato italiano.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Ogni funzionalità è stata pensata per risolvere problemi reali dello studio: 
                dalla gestione delle scadenze alla comunicazione con i clienti, 
                dall'organizzazione del lavoro alla conformità normativa.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-slate-700">
                  <Check className="w-5 h-5 text-teal-500" />
                  <span>Interfaccia in italiano</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Check className="w-5 h-5 text-teal-500" />
                  <span>Normativa italiana integrata</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Check className="w-5 h-5 text-teal-500" />
                  <span>Supporto dedicato</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-slate-400/20 rounded-3xl blur-3xl" />
              <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 border border-slate-200">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Ascoltiamo i Professionisti</h4>
                      <p className="text-slate-600 text-sm">Feedback continuo da notai attivi per migliorare ogni aspetto</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Scale className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Conformità Garantita</h4>
                      <p className="text-slate-600 text-sm">Aggiornamenti normativi costanti e template sempre conformi</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Innovazione Utile</h4>
                      <p className="text-slate-600 text-sm">AI e automazione solo dove servono davvero, mai fine a sé stesse</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Il Nostro Impegno */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Il Nostro Impegno
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Valori e garanzie che ci guidano ogni giorno nel supportare il tuo studio
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-amber-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Sicurezza Totale</h3>
              <p className="text-slate-600 text-sm">Crittografia end-to-end, server EU, backup automatici ogni giorno</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-teal-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Assistenza Italiana</h3>
              <p className="text-slate-600 text-sm">Team di supporto italiano, risposte rapide, assistenza AI h24</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-purple-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Sempre Aggiornato</h3>
              <p className="text-slate-600 text-sm">Nuove funzionalità e aggiornamenti normativi inclusi nel canone</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-rose-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-7 h-7 text-rose-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Migrazione Gratuita</h3>
              <p className="text-slate-600 text-sm">Ti aiutiamo a importare i dati dal tuo gestionale attuale senza costi</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Perché Sceglierci */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Perché Scegliere Studio Notarile AI
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              I vantaggi concreti che trasformano la gestione del tuo studio
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-200/50 group-hover:scale-110 transition-transform">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-slate-900 mb-2">-70%</h3>
              <p className="text-slate-600 font-medium">Tempo Amministrativo</p>
              <p className="text-slate-500 text-sm mt-1">Automatizza le attività ripetitive</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-200/50 group-hover:scale-110 transition-transform">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-slate-900 mb-2">5 min</h3>
              <p className="text-slate-600 font-medium">Per Generare un Atto</p>
              <p className="text-slate-500 text-sm mt-1">Con l'editor AI integrato</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-200/50 group-hover:scale-110 transition-transform">
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-slate-900 mb-2">€0</h3>
              <p className="text-slate-600 font-medium">Costi Infrastruttura</p>
              <p className="text-slate-500 text-sm mt-1">Niente server, niente manutenzione</p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-rose-200/50 group-hover:scale-110 transition-transform">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-4xl font-bold text-slate-900 mb-2">100%</h3>
              <p className="text-slate-600 font-medium">Team Coordinato</p>
              <p className="text-slate-500 text-sm mt-1">Tutti allineati in tempo reale</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Domande Frequenti
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
                >
                  <span className="font-medium text-slate-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Demo Form */}
      <section id="demo" className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: CTA Text */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Pronto a Trasformare il Tuo Studio?
              </h2>
              <p className="text-xl text-slate-300 mb-8 max-w-xl">
                Richiedi una demo personalizzata. Ti contatteremo entro 24 ore per mostrarti 
                come Studio Notarile AI può semplificare il tuo lavoro.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>Demo gratuita</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>Senza impegno</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>Assistenza dedicata</span>
                </div>
              </div>
            </div>
            
            {/* Right: Demo Form */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              {demoSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Richiesta Inviata!</h3>
                  <p className="text-slate-600 mb-6">
                    Grazie per il tuo interesse. Ti contatteremo entro 24 ore lavorative.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={() => { setDemoSuccess(false); setDemoForm({ nome_studio: '', nome_referente: '', cognome_referente: '', email: '', telefono: '', citta: '', numero_dipendenti: '', messaggio: '' }); }}
                  >
                    Invia un'altra richiesta
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-slate-800 mb-6">Richiedi una Demo</h3>
                  <form onSubmit={handleDemoSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nome_referente">Nome *</Label>
                        <Input
                          id="nome_referente"
                          value={demoForm.nome_referente}
                          onChange={(e) => setDemoForm({...demoForm, nome_referente: e.target.value})}
                          placeholder="Mario"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="cognome_referente">Cognome *</Label>
                        <Input
                          id="cognome_referente"
                          value={demoForm.cognome_referente}
                          onChange={(e) => setDemoForm({...demoForm, cognome_referente: e.target.value})}
                          placeholder="Rossi"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="nome_studio">Nome Studio *</Label>
                      <Input
                        id="nome_studio"
                        value={demoForm.nome_studio}
                        onChange={(e) => setDemoForm({...demoForm, nome_studio: e.target.value})}
                        placeholder="Studio Notarile Rossi"
                        required
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={demoForm.email}
                          onChange={(e) => setDemoForm({...demoForm, email: e.target.value})}
                          placeholder="info@studiorossi.it"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="telefono">Telefono</Label>
                        <Input
                          id="telefono"
                          value={demoForm.telefono}
                          onChange={(e) => setDemoForm({...demoForm, telefono: e.target.value})}
                          placeholder="+39 049 123 4567"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="citta">Città</Label>
                        <Input
                          id="citta"
                          value={demoForm.citta}
                          onChange={(e) => setDemoForm({...demoForm, citta: e.target.value})}
                          placeholder="Padova"
                        />
                      </div>
                      <div>
                        <Label htmlFor="numero_dipendenti">Dipendenti</Label>
                        <Input
                          id="numero_dipendenti"
                          value={demoForm.numero_dipendenti}
                          onChange={(e) => setDemoForm({...demoForm, numero_dipendenti: e.target.value})}
                          placeholder="1-5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="messaggio">Messaggio (opzionale)</Label>
                      <Textarea
                        id="messaggio"
                        value={demoForm.messaggio}
                        onChange={(e) => setDemoForm({...demoForm, messaggio: e.target.value})}
                        placeholder="Raccontaci le tue esigenze..."
                        rows={3}
                      />
                    </div>
                    <Button 
                      type="submit"
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold py-6"
                      disabled={demoLoading}
                    >
                      {demoLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Richiedi Demo Gratuita
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Compilando il form accetti la nostra <a href="/privacy-policy" className="text-amber-600 hover:underline">Privacy Policy</a>
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-6 h-6 text-amber-400" />
                <span className="text-white font-semibold">Studio Notarile AI</span>
              </div>
              <p className="text-sm">
                Il gestionale intelligente per i notai italiani del futuro.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Prodotto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Funzionalità</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Prezzi</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Supporto</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+390497852233" className="hover:text-white transition-colors">+39 049 785 2233</a>
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  <a href="https://robottino.telcavoip.net/it/outstanding/user/businesscard/?&id=152854922" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Assistenza AI h24</a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Termini di Servizio</a></li>
                <li><a href="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">© 2026 Studio Notarile AI. Tutti i diritti riservati.</p>
            <a 
              href="https://telcavoip.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm hover:text-teal-400 transition-colors"
            >
              <span>❤️</span>
              <span>Powered With Love By <strong className="text-teal-500">TelcaVoIP AI</strong></span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
